/**
 * Per-target report state: which targets already have one, requesting a new one,
 * and polling the ones in flight.
 *
 * Its own hook rather than more state in `useLeaderboard`, because the two have
 * different shapes. The board is fetched per page and per sort; reports are
 * fetched once per run — the button state is needed for every visible row, so one
 * request per run beats one per target — and then *polled*, which nothing else in
 * this UI does.
 *
 * Polling only while something is `queued` or `running`. A report takes 5–15
 * minutes in another container and the row in Aurora is the only channel back, so
 * there is nothing to subscribe to; but a timer that keeps firing after the last
 * report finished is a request every ten seconds for the life of the tab.
 *
 * **More than one run at a time, on purpose.** The API has no cross-run report
 * list, and the cumulative board offers a report per row — for the newest run that
 * ranked that target, which is generally *not* the run selected in the sidebar. So
 * this tracks a set of runs: the selected one always, plus any a caller registers
 * with `track()`. Merging them is safe because `leaderboard.targets.id` is a
 * bigserial, so a target id belongs to exactly one run and `byTarget` cannot
 * confuse two.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  downloadReport as fetchReportFile,
  listReports,
  requestReport,
  type TokenGetter,
} from '../api/leaderboard'
import type { ReportRow } from '../api/types'
import { REPORT_IN_FLIGHT } from '../api/types'

/** How often to re-read the rows while any report is in flight. */
const POLL_MS = 10_000

export interface ReportsState {
  /** The newest report per target id — what the button renders from. */
  byTarget: Map<number, ReportRow>
  /**
   * Every report by its own id, for the `?report=` link in the email. The link
   * carries the report id and not the target id, because the recipient wants the
   * document they were mailed, which may not be the newest one for that target.
   */
  byId: Map<number, ReportRow>
  /** Target id whose POST is in flight. */
  requesting: number | null
  /** Report id whose download is in flight. */
  downloading: number | null
  /**
   * Why the last request or download failed, with the target it was for. Cleared
   * when the next one starts. Keyed so a failure shows on the row that caused it
   * rather than as a page-level banner over an otherwise fine board.
   */
  error: { targetId: number; message: string } | null
  /** The API's own sentence about the last successful request. */
  notice: string | null
  /**
   * Request a report. `runId` names the run to assess when it is not the selected
   * one — the board's rows are cross-run, so the caller supplies it from the
   * observation it is offering a report on.
   */
  request: (targetId: number, runId?: string) => Promise<void>
  download: (report: ReportRow) => Promise<void>
  /**
   * Also list (and poll) this run's reports. Idempotent and cheap to call from a
   * render effect: a run already tracked returns the same state, so it does not
   * re-render.
   */
  track: (runId: string) => void
  dismissNotice: () => void
}

export function useReports(
  runId: string | null,
  getToken: TokenGetter,
  enabled: boolean,
): ReportsState {
  const [rows, setRows] = useState<ReportRow[]>([])
  const [tracked, setTracked] = useState<string[]>([])
  const [requesting, setRequesting] = useState<number | null>(null)
  const [downloading, setDownloading] = useState<number | null>(null)
  const [error, setError] = useState<{ targetId: number; message: string } | null>(
    null,
  )
  const [notice, setNotice] = useState<string | null>(null)

  // The run set as a single primitive, so `load` and the effects below have a
  // dependency that changes only when the set really does. A string rather than
  // the array: a new array every render would rebuild `load` every render, and
  // the poll effect depends on it.
  const runsKey = useMemo(() => {
    const ids = new Set<string>()
    if (runId) ids.add(runId)
    for (const id of tracked) ids.add(id)
    return [...ids].join('\n')
  }, [runId, tracked])

  // Whether a poll is warranted, read by the interval effect without being one of
  // its dependencies — as a dependency it would tear down and rebuild the timer
  // on every poll that changed a row, which resets the interval each time.
  const inFlight = rows.some((r) => REPORT_IN_FLIGHT.includes(r.status))
  const inFlightRef = useRef(inFlight)
  inFlightRef.current = inFlight

  const load = useCallback(
    async (signal?: AbortSignal) => {
      const ids = runsKey ? runsKey.split('\n') : []
      if (ids.length === 0) return
      const pages = await Promise.all(
        // Per run, and each failure swallowed on its own: one unreadable run must
        // not blank the buttons for the others.
        ids.map((id) =>
          listReports(id, getToken, signal).then(
            (page) => page.reports,
            // Swallowed on purpose, and never surfaced as a page error: this is
            // button state beside a ranking that is itself fine, and an empty map
            // renders as "no report yet", which is the safe reading. A 401 here is
            // the same 401 `useLeaderboard` is about to raise, and it owns the
            // sign-in gate — two hooks fighting over it would flicker.
            () => [] as ReportRow[],
          ),
        ),
      )
      if (signal?.aborted) return
      // Sorted by id descending rather than trusting the per-run order: each page
      // arrives newest-first, but merging two pages interleaves them, and
      // `byTarget` below takes the FIRST row it sees per target. `id` is a
      // bigserial, so descending id is newest-first across runs.
      setRows(pages.flat().sort((a, b) => b.id - a.id))
    },
    [runsKey, getToken],
  )

  // ── Load every run in the set ─────────────────────────────────────────────
  // `runsKey` changes when the selection changes, so this refetches and replaces
  // `rows` wholesale. Deliberately without clearing them first: a target id
  // identifies exactly one run's row, so a row surviving from the previous
  // selection cannot be attributed to a target now on screen — and clearing would
  // also throw away the board rows `tracked` was registered to keep.
  useEffect(() => {
    if (!enabled) {
      setRows([])
      setNotice(null)
      setError(null)
      return
    }
    if (!runsKey) return
    const ac = new AbortController()
    void load(ac.signal)
    return () => ac.abort()
  }, [enabled, runsKey, load])

  // ── Poll while anything is queued or running ──────────────────────────────
  useEffect(() => {
    if (!enabled || !runsKey || !inFlight) return
    const timer = window.setInterval(() => {
      // Re-checked at fire time, not only at setup: the last poll may have moved
      // the final report to `sent`, and one wasted request is better than a
      // timer whose teardown races the state update that ended the work.
      if (inFlightRef.current) void load()
    }, POLL_MS)
    return () => window.clearInterval(timer)
    // `inFlight` is a dependency so the timer STARTS when a report is queued;
    // the ref above is what keeps it from restarting on every row change.
  }, [enabled, runsKey, inFlight, load])

  const track = useCallback((id: string) => {
    if (!id) return
    // Returning `prev` unchanged when it is already there is what makes this safe
    // to call from an effect on every render: React bails out of the re-render.
    setTracked((prev) => (prev.includes(id) ? prev : [...prev, id]))
  }, [])

  const request = useCallback(
    async (targetId: number, forRunId?: string) => {
      const target = forRunId || runId
      if (!target) return
      setRequesting(targetId)
      setError(null)
      setNotice(null)
      try {
        const result = await requestReport(target, targetId, getToken)
        // Merged rather than refetched: the POST returns the authoritative row
        // (the existing one when this was a second click), and a refetch would
        // race the insert on a read replica.
        setRows((prev) => [
          result.report,
          ...prev.filter((r) => r.id !== result.report.id),
        ])
        // Polling reads whole runs, so the run this was filed under has to be in
        // the set or the merged row would never advance past `queued`.
        setTracked((prev) => (prev.includes(target) ? prev : [...prev, target]))
        setNotice(result.message)
      } catch (e) {
        // The API's `detail` is written for the user — a 403 explains that
        // reports need authentication, a 400 that the address is outside the
        // allowed domains. Both are answers, not stack traces, so show them.
        setError({
          targetId,
          message: e instanceof Error ? e.message : String(e),
        })
      } finally {
        setRequesting(null)
      }
    },
    [runId, getToken],
  )

  const download = useCallback(
    async (report: ReportRow) => {
      setDownloading(report.id)
      setError(null)
      try {
        await fetchReportFile(report.id, getToken)
      } catch (e) {
        setError({
          targetId: report.target_id,
          message: e instanceof Error ? e.message : String(e),
        })
      } finally {
        setDownloading(null)
      }
    },
    [getToken],
  )

  const dismissNotice = useCallback(() => setNotice(null), [])

  // Newest per target, `rows` being sorted newest-first above. A target may
  // legitimately have several finished reports, since asking again after a rubric
  // change is allowed.
  const byTarget = new Map<number, ReportRow>()
  const byId = new Map<number, ReportRow>()
  for (const row of rows) {
    byId.set(row.id, row)
    if (!byTarget.has(row.target_id)) byTarget.set(row.target_id, row)
  }

  return {
    byTarget,
    byId,
    requesting,
    downloading,
    error,
    notice,
    request,
    download,
    track,
    dismissNotice,
  }
}
